import {Roles} from 'app/Constants';
import {LookupProps} from 'components/grid-dx/MasterGrid';
import IntegrityRoleGrid from 'components/UserRole';
import {Column, Lookup} from 'devextreme-react/data-grid';
import {i18n2} from 'features/i18n/i18n';
import {useLoadLookupProfiles} from 'features/profile/hooks';
import useLoadLookupSectors from 'features/sectors/hooks/useLoadLookupSectors';
import React from 'react';
import useLoadLookupShift from '../../turn/hooks/useLoadLookupShift';
import {
  useAddCollaborator,
  useLoadCollaborator,
  useUpdateCollaborator,
} from '../hooks/index';
import {CollaboSchema} from '../model/Collaborator';
import Buttons, {MAX_ACTION_BUTTONS} from './Buttons';
import {IntegrityCollaboGrid} from './index';
import {useUtilInformation} from 'components/hooks';
import {useIsTablet} from 'components/hooks/useIsTablet';
import {getActionColumnWidth} from 'components/grid-dx/styles/actionButtonStyles';
import {useLoadParameter} from 'features/parameter/hook/useLoadParameter';

export const lookupActive = {
  store: {
    type: 'array',
    data: [
      {id: 0, name: i18n2._('Desactivado')},
      {id: 1, name: i18n2._('Activado')},
      {id: 2, name: i18n2._('En licencia')},
    ],
    key: 'id',
  },
};

export const lookupRole = {
  store: {
    type: 'array',
    data: [
      {id: 3, name: i18n2._('Collaborator')},
      {id: 0, name: i18n2._('Admin ITPS')},
      {id: 1, name: i18n2._('Admin QA')},
      {id: 2, name: i18n2._('Admin HSE')},
      {id: 4, name: i18n2._('Trainer')},
    ],
    key: 'id',
  },
};

const CollaboGrid = () => {
  const loadCollabo = useLoadCollaborator();
  const addCollabo = useAddCollaborator();
  const updateCollabo = useUpdateCollaborator();
  const {token, cwid, role} = useUtilInformation();
  const loadSectors = useLoadLookupSectors({token, cwid, role});
  const loadProfiles = useLoadLookupProfiles();
  const loadTurns = useLoadLookupShift();
  const isTablet = useIsTablet();
  const {data: settings, loading, error} = useLoadParameter();
  if (loading) return null;
  if (error) return null;

  const schema = CollaboSchema(false, settings);

  return (
    <IntegrityRoleGrid
      allowEdit={[Roles.AdminITPS, Roles.AdminQA]}
      onlyEdit={false}
      schema={schema}
      dataSource={loadCollabo}
      promiseAdd={addCollabo}
      promiseEdit={updateCollabo}
      exportName="Collaborator-export"
      id={['employee_Nr']}
      columnOptions={columnOptions}
      remoteLookup={[
        {
          column: 'iD_Sector',
          keyValue: 'iD_Sector',
          displayValue: 'name',
          saveDisplayValue: 'sector_Name',
          dataSource: loadSectors,
        },
        {
          column: 'iD_Profile_Web',
          keyValue: 'iD_Profile',
          displayValue: 'name',
          saveDisplayValue: 'profile_Name',
          dataSource: loadProfiles,
        },
        {
          column: 'iD_Shift',
          keyValue: 'iD_Shift',
          displayValue: 'name',
          saveDisplayValue: 'shift_Name',
          dataSource: loadTurns,
        },
      ]}
      integrityGrid={IntegrityCollaboGrid}
      integrityId={['Employee_Nr']}
      integrityName={'Collaborator'}>
      <Column
        caption={i18n2._('Numero de empleado')}
        dataField="employee_Nr"
        allowEditing={false}
        defaultSortOrder={'desc'}
      />
      <Column
        caption={i18n2._('Meta4 Employee Id')}
        dataField="meta4EmployeeId"
        allowEditing={false}
        formItem={{visible: false}}
      />
      <Column caption={i18n2._('Nombre')} dataField="name" width={200} />
      <Column caption={i18n2._('CWID')} dataField="cwid" />
      <Column
        caption={i18n2._('#Sector')}
        dataField="iD_Sector"
        visible={false}
      />
      <Column
        caption={i18n2._('#Perfil')}
        dataField="iD_Profile_Web"
        visible={false}
      />
      <Column
        caption={i18n2._('#Turno')}
        dataField="iD_Shift"
        visible={false}
      />
      <Column caption={i18n2._('Sector')} dataField="sector_Name" />
      <Column caption={i18n2._('Perfil')} dataField="profile_Name" />
      <Column caption={i18n2._('Turno')} dataField="shift_Name" />
      <Column caption={i18n2._('Role')} dataField="role" visible={false}>
        <Lookup
          dataSource={lookupRole}
          valueExpr="id"
          displayExpr={(item: LookupProps) => {
            return i18n2._(item ? item.name : '');
          }}
        />
      </Column>
      <Column
        caption={i18n2._('Externo')}
        dataField="external"
        dataType="boolean"
        width={90}
        alignment="center"
      />
      <Column caption={i18n2._('Estado')} dataField="active">
        <Lookup
          dataSource={lookupActive}
          valueExpr="id"
          displayExpr={(item: LookupProps) => {
            return i18n2._(item ? item.name : '');
          }}
        />
      </Column>
      <Column caption={i18n2._('Email')} dataField="email" />
      <Column
        caption={i18n2._('Capacitación Inicial')}
        dataField="capa_Initial"
        visible={false}
      />
      <Column
        caption={i18n2._('Alta en el sistema')}
        dataField="onboard_Date"
        visible={false}
        dataType="date"
        format="dd/MM/yyyy"
        width="8%"
      />
      <Column
        caption={i18n2._('Fecha de contratación')}
        dataField="hire_Date"
        visible={false}
        dataType="date"
        format="dd/MM/yyyy"
        width="8%"
      />
      <Column caption={i18n2._('Revision')} dataField="revision" />
      <Column caption={i18n2._('Motivo')} dataField="reason" />
      <Column
        caption={i18n2._('Fecha de actualizacion')}
        dataField="updateDate"
        visible={false}
        dataType="date"
        format="dd/MM/yyyy"
        calculateCellValue={(row: any) => new Date(row.updateDate)}
      />
      <Column
        caption={i18n2._('Actualizado por')}
        dataField="updatedBy"
        visible={false}
      />
      <Column
        caption=""
        cellComponent={(e: any) => (
          <Buttons
            cwid={e.data.data.cwid}
            capa_Initial={e.data.data.capa_Initial || e.data.data.capa_spac_ini}
            employee_Nr={e.data.data.employee_Nr}
          />
        )}
        width={getActionColumnWidth(MAX_ACTION_BUTTONS, isTablet)}
        fixed
        alignment="right"
      />
    </IntegrityRoleGrid>
  );
};

const columnOptions = [
  //size = "small", rows = cantidad
  {
    sizeDesktop: 12,
    column: 'employee_Nr',
    label: i18n2._('#Empleado'),
    required: true,
    allowEdit: false,
    hiddenAdd: false,
  },
  {
    column: 'name',
    multiline: true,
    required: true,
    sizeMobile: 12,
    sizeDesktop: 12,
    allowEdit: true,
  },
  {
    column: 'meta4EmployeeId',
    allowEdit: false,
    hiddenAdd: true,
    hiddenEdit: true,
    sizeMobile: 12,
    sizeDesktop: 12,
  },
  {
    column: 'iD_Sector',
    myLabel: i18n2._('Sector'),
    allowEdit: true,
    required: true,
  },
  {
    column: 'iD_Shift',

    required: true,
    myLabel: i18n2._('Turno'),
    allowEdit: true,
  },
  {
    column: 'cwid',
    allowEdit: true,
  },
  {
    column: 'onboard_Date',
    hiddenAdd: true,
    hiddenEdit: true,
    allowEdit: true,
  },
  {
    column: 'hire_Date',
    allowEdit: true,
  },
  {
    column: 'sector_Name',
    hiddenAdd: true,
    hiddenEdit: true,
    required: true,
    allowEdit: true,
  },
  {
    column: 'profile_Name',
    hiddenAdd: true,
    hiddenEdit: true,
    required: true,
    allowEdit: true,
  },
  {
    column: 'shift_Name',
    hiddenAdd: true,
    hiddenEdit: true,
    allowEdit: true,
  },
  {
    column: 'capa_Initial',
    sizeMobile: 12,
    sizeDesktop: 4,
    allowEdit: true,
  },
  {
    column: 'external',
    sizeMobile: 12,
    sizeDesktop: 4,
    allowEdit: true,
  },
  {
    column: 'password',
    sizeMobile: 12,
    allowEdit: true,
    required: true,
    sizeDesktop: 6,
  },
  {
    column: 'email',
    sizeMobile: 12,
    sizeDesktop: 6,
    allowEdit: true,
  },
  {
    selectType: 'limitedByColumn',
    column: 'iD_Profile_Web',
    myLabel: i18n2._('Perfil'),
    keyForFilter: 'iD_Sector',
    allowEdit: true,
  },
  {
    selectType: 'enum',
    allowEdit: true,
    column: 'role',
    data: [
      {
        key: 0,
        value: 'AdminITPS',
      },
      {
        key: 1,
        value: 'AdminQA',
      },
      {
        key: 2,
        value: 'AdminHSE',
      },
      {
        key: 3,
        value: i18n2._('Colaborador'),
      },
      {
        key: 4,
        value: i18n2._('Trainer'),
      },
    ],
  },
  {
    selectType: 'enum',
    column: 'active',
    sizeMobile: 12,
    sizeDesktop: 6,
    hiddenAdd: true,
    allowEdit: true,
    data: [
      {
        key: 1,
        value: i18n2._('Activo'),
      },
      {
        key: 0,
        value: i18n2._('Inactivo'),
      },
      {
        key: 2,
        value: i18n2._('En licencia'),
      },
    ],
  },
  {
    selectType: 'PDF',
    column: 'pdfName',
    sizeMobile: 12,
    hiddenAdd: true,
    sizeDesktop: 12,
  },
];

export default CollaboGrid;
